import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, useToast, Link,
} from '@chakra-ui/react';
import { decoracionApi, localidadesApi, TIPOS_VIALIDAD, Localidad } from '../../../api/serviciosApi';
import MapSelector from '../../../components/MapSelector';
import EvidenciasInline from '../EvidenciasInline';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const ESTATUS = ['Pendiente', 'Programada', 'En proceso', 'Finalizada', 'Cancelada'];

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor="#5b182b" pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color="#5b182b" textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm">{value || '—'}</Text>
  </Box>
);

const fmt = (v?: string) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const dateVal = (v?: string) => (v ? String(v).split('T')[0] : '');
const numOrUndef = (v: string) => (v === '' ? undefined : Number(v));

const FormularioDecoracion: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();

  const [folio, setFolio] = useState(data?.folio || '');
  const [evento, setEvento] = useState(data?.evento || '');
  const [fechaInicio, setFechaInicio] = useState(dateVal(data?.fechaInicio) || new Date().toISOString().split('T')[0]);
  const [fechaTermino, setFechaTermino] = useState(dateVal(data?.fechaTermino));
  const [responsable, setResponsable] = useState(data?.responsable || '');
  const [descripcion, setDescripcion] = useState(data?.descripcion || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [estado, setEstado] = useState(data?.estado || 'Tlaxcala');
  const [municipio, setMunicipio] = useState(data?.municipio || 'Totolac');
  const [localidad, setLocalidad] = useState(data?.localidad || '');
  const [colonia, setColonia] = useState(data?.colonia || '');
  const [codigoPostal, setCodigoPostal] = useState(data?.codigoPostal || '');
  const [tipoVialidad, setTipoVialidad] = useState(data?.tipoVialidad || '');
  const [vialidad, setVialidad] = useState(data?.vialidad || '');
  const [numeroExterior, setNumeroExterior] = useState(data?.numeroExterior || '');
  const [numeroInterior, setNumeroInterior] = useState(data?.numeroInterior || '');
  const [referencias, setReferencias] = useState(data?.referencias || '');
  const [latitud, setLatitud] = useState(data?.latitud != null ? String(data.latitud) : '');
  const [longitud, setLongitud] = useState(data?.longitud != null ? String(data.longitud) : '');
  const [urlMaps, setUrlMaps] = useState(data?.urlMaps || '');
  const [estatus, setEstatus] = useState(data?.estatus || 'Pendiente');
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    localidadesApi.list().then((res) => setLocalidades(res || [])).catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!evento) {
      toast({ title: 'Campos obligatorios', description: 'Indica el nombre del evento.', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const body = {
        folio: folio || undefined,
        evento,
        fechaInicio: fechaInicio || undefined,
        fechaTermino: fechaTermino || undefined,
        responsable: responsable || undefined,
        descripcion: descripcion || undefined,
        observaciones: observaciones || undefined,
        estado: estado || undefined,
        municipio: municipio || undefined,
        localidad: localidad || undefined,
        colonia: colonia || undefined,
        codigoPostal: codigoPostal || undefined,
        tipoVialidad: tipoVialidad || undefined,
        vialidad: vialidad || undefined,
        numeroExterior: numeroExterior || undefined,
        numeroInterior: numeroInterior || undefined,
        referencias: referencias || undefined,
        latitud: numOrUndef(latitud),
        longitud: numOrUndef(longitud),
        urlMaps: urlMaps || undefined,
        estatus,
      };
      if (modo === 'edit') {
        await decoracionApi.update(data.id, body);
      } else {
        await decoracionApi.create(body);
      }
      toast({ title: modo === 'edit' ? 'Evento actualizado' : 'Evento creado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'Error al guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const d = data || {};
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Información del Evento</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="FOLIO" value={d.folio} />
          <Field label="ESTATUS" value={<Badge>{d.estatus}</Badge>} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="EVENTO" value={d.evento} /></Box>
          <Field label="FECHA INICIO" value={fmt(d.fechaInicio)} />
          <Field label="FECHA TÉRMINO" value={fmt(d.fechaTermino)} />
          <Field label="RESPONSABLE" value={d.responsable} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="DESCRIPCIÓN" value={d.descripcion} /></Box>
        </SimpleGrid>
        <SectionTitle>Ubicación</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          <Field label="ESTADO" value={d.estado} />
          <Field label="MUNICIPIO" value={d.municipio} />
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="C.P." value={d.codigoPostal} />
          <Field label="VIALIDAD" value={[d.tipoVialidad, d.vialidad].filter(Boolean).join(' ')} />
          <Field label="No. EXT / INT" value={[d.numeroExterior, d.numeroInterior].filter(Boolean).join(' / ')} />
          <Field label="UBICACIÓN" value={d.latitud != null && d.longitud != null ? (
            <Link color="blue.500" isExternal href={d.urlMaps || `https://www.google.com/maps?q=${d.latitud},${d.longitud}`}>Ver en Google Maps</Link>
          ) : undefined} />
          <Box gridColumn={{ md: 'span 2' }}><Field label="REFERENCIAS" value={d.referencias} /></Box>
        </SimpleGrid>
        {d.observaciones && (<><SectionTitle>Observaciones</SectionTitle><Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text></>)}
        <EvidenciasInline archivos={d.archivos} />
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Información del Evento</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Folio</FormLabel>
          <Input placeholder="(Automático si se deja vacío)" value={folio} onChange={(e) => setFolio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={estatus} onChange={(e) => setEstatus(e.target.value)}>
            {ESTATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Evento</FormLabel>
          <Input placeholder="Nombre del evento a decorar" value={evento} onChange={(e) => setEvento(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha inicio</FormLabel>
          <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha término</FormLabel>
          <Input type="date" value={fechaTermino} onChange={(e) => setFechaTermino(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input placeholder="Nombre del responsable" value={responsable} onChange={(e) => setResponsable(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea rows={3} placeholder="Descripción de la decoración..." value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Ubicación</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Estado</FormLabel>
          <Input value={estado} onChange={(e) => setEstado(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Municipio</FormLabel>
          <Input value={municipio} onChange={(e) => setMunicipio(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona localidad" value={localidad} onChange={(e) => setLocalidad(e.target.value)}>
            {localidades.map((loc) => <option key={loc.id} value={loc.descripcion}>{loc.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={colonia} onChange={(e) => setColonia(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Código postal</FormLabel>
          <Input value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Select placeholder="Selecciona tipo" value={tipoVialidad} onChange={(e) => setTipoVialidad(e.target.value)}>
            {TIPOS_VIALIDAD.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Vialidad</FormLabel>
          <Input placeholder="Nombre de la vialidad" value={vialidad} onChange={(e) => setVialidad(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. exterior</FormLabel>
          <Input value={numeroExterior} onChange={(e) => setNumeroExterior(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">No. interior</FormLabel>
          <Input value={numeroInterior} onChange={(e) => setNumeroInterior(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input placeholder="Entre calles, puntos de referencia..." value={referencias} onChange={(e) => setReferencias(e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Ubicación en el mapa</FormLabel>
          <MapSelector
            initialLat={latitud ? Number(latitud) : null}
            initialLng={longitud ? Number(longitud) : null}
            onLocationSelect={(lat, lng, address) => {
              setLatitud(String(lat));
              setLongitud(String(lng));
              setUrlMaps(`https://www.google.com/maps?q=${lat},${lng}`);
              if (address?.neighborhood && !colonia) setColonia(address.neighborhood);
              if (address?.postalCode && !codigoPostal) setCodigoPostal(address.postalCode);
            }}
          />
          {latitud && longitud && (
            <Link fontSize="sm" color="blue.500" isExternal mt={1} display="inline-block"
              href={urlMaps || `https://www.google.com/maps?q=${latitud},${longitud}`}>
              Ver en Google Maps ({latitud}, {longitud})
            </Link>
          )}
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Observaciones</SectionTitle>
      <FormControl>
        <Textarea rows={2} placeholder="Notas u observaciones generales..." value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
      </FormControl>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioDecoracion;
